'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Copy, Check, ArrowRight, Building2, Layers, HardHat } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignUpFormData {
  fullName: string;
  email: string;
  role: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

const DEMO_USERS = [
  { name: 'Gopal Sharma', role: 'Admin', email: 'gopal.sharma@jbw.co.in', password: 'Admin@JBW2026' },
  { name: 'Manish Dadhich', role: 'Project Manager', email: 'manish.dadhich@jbw.co.in', password: 'PM@JBW2026' },
  { name: 'Sonu Tiwari', role: 'Contractor', email: 'sonu.tiwari@jbw.co.in', password: 'Site@JBW2026' },
];

export default function LoginContent() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signUp } = useAuth();
  const router = useRouter();

  const loginForm = useForm<LoginFormData>({ defaultValues: { email: '', password: '', rememberMe: false } });
  const signupForm = useForm<SignUpFormData>({ defaultValues: { fullName: '', email: '', role: 'Project Manager', password: '', confirmPassword: '', agreeTerms: false } });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofill = (user: typeof DEMO_USERS[0]) => {
    loginForm.setValue('email', user.email);
    loginForm.setValue('password', user.password);
    toast.info(`Autofilled credentials for ${user.name} (${user.role})`);
  };

  const onLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back!');
      router.push('/dashboard');
    } catch (error: any) {
      loginForm.setError('email', { message: error?.message || 'Invalid credentials — use the demo accounts below to sign in' });
    } finally {
      setIsLoading(false);
    }
  };

  const onSignup = async (data: SignUpFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.fullName, role: data.role });
      toast.success('Account created! You can now sign in.');
      setMode('login');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[40%] bg-primary flex-col justify-between p-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 rounded-full border-2 border-white" />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full border border-white" />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 rounded-full border border-white" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Building2 size={22} className="text-white" />
            </div>
            <div>
              <span className="text-white font-bold text-xl">JBW ERP</span>
              <p className="text-white/50 text-xs">Jangra Buildwave</p>
            </div>
          </div>
          <div className="space-y-6">
            <h2 className="text-hero-xl font-bold text-white leading-snug">
              Manage every build.<br />Track every rupee.
            </h2>
            <p className="text-white/60 text-base leading-relaxed">
              From Bhiwadi to Neemrana — JBW ERP keeps your construction, interior, and building projects on budget and on schedule.
            </p>
          </div>
          <div className="mt-10 space-y-4">
            {[
              { icon: <Layers size={18} />, text: 'Project Control Center with Kanban & Gantt' },
              { icon: <HardHat size={18} />, text: 'Daily Progress Reports from site, on mobile' },
              { icon: <Building2 size={18} />, text: 'AR/AP Billing with GST — INR native' },
            ].map((item, i) => (
              <div key={`feature-${i}`} className="flex items-center gap-3 text-white/70 text-sm">
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                {item.text}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <p className="text-white/30 text-xs">© 2026 Jangra Buildwave Pvt Ltd · Bhiwadi, Rajasthan</p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-md">
          {/* Mode Toggle */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${mode === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${mode === 'signup' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
            >
              Sign Up
            </button>
          </div>

          {mode === 'login' ? (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground">Welcome back</h1>
                <p className="text-sm text-muted-foreground mt-1">Sign in to your JBW ERP account</p>
              </div>
              <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
                <div>
                  <label className="label">Email address</label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder="you@jbw.co.in"
                    {...loginForm.register('email', { required: 'Email is required' })}
                  />
                  {loginForm.formState.errors.email && (
                    <p className="error-text">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="label">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="input-field pr-10"
                      placeholder="Enter your password"
                      {...loginForm.register('password', { required: 'Password is required' })}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="error-text">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                    <input type="checkbox" {...loginForm.register('rememberMe')} className="rounded" />
                    Remember me
                  </label>
                  <button type="button" className="text-sm text-primary hover:underline font-medium">
                    Forgot password?
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn-primary justify-center py-3 text-base"
                  style={{ minWidth: '160px' }}
                >
                  {isLoading ? (
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : (
                    <>Sign In <ArrowRight size={16} /></>
                  )}
                </button>
              </form>

              {/* Demo Credentials */}
              <div className="mt-6 border border-border rounded-xl overflow-hidden">
                <div className="bg-muted px-4 py-2.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Demo Accounts</span>
                  <span className="text-xs text-muted-foreground">Click a row to autofill</span>
                </div>
                <div className="divide-y divide-border">
                  {DEMO_USERS.map((user) => (
                    <div
                      key={`demo-${user.email}`}
                      className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => autofill(user)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${user.role === 'Admin' ? 'bg-primary' : user.role === 'Project Manager' ? 'bg-accent' : 'bg-warning'}`}>
                          {user.name[0]}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground">{user.name}</p>
                          <p className="text-xs text-muted-foreground">{user.role}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground hidden sm:block">{user.email}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleCopy(user.email, `email-${user.email}`); }}
                          className="p-1 rounded hover:bg-border text-muted-foreground"
                          title="Copy email"
                        >
                          {copiedField === `email-${user.email}` ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground">Create account</h1>
                <p className="text-sm text-muted-foreground mt-1">Join JBW ERP — your admin will activate your access</p>
              </div>
              <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
                <div>
                  <label className="label">Full Name</label>
                  <input type="text" className="input-field" placeholder="Sonu Tiwari" {...signupForm.register('fullName', { required: 'Full name is required' })} />
                  {signupForm.formState.errors.fullName && <p className="error-text">{signupForm.formState.errors.fullName.message}</p>}
                </div>
                <div>
                  <label className="label">Work Email</label>
                  <input type="email" className="input-field" placeholder="you@jbw.co.in" {...signupForm.register('email', { required: 'Email is required' })} />
                  {signupForm.formState.errors.email && <p className="error-text">{signupForm.formState.errors.email.message}</p>}
                </div>
                <div>
                  <label className="label">Your Role</label>
                  <select className="input-field" {...signupForm.register('role', { required: true })}>
                    <option value="Project Manager">Project Manager</option>
                    <option value="Contractor">Contractor / Site Supervisor</option>
                    <option value="Admin">Admin / Owner</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Password</label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} className="input-field pr-8" {...signupForm.register('password', { required: 'Required', minLength: { value: 8, message: 'Min 8 chars' } })} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    {signupForm.formState.errors.password && <p className="error-text">{signupForm.formState.errors.password.message}</p>}
                  </div>
                  <div>
                    <label className="label">Confirm</label>
                    <div className="relative">
                      <input type={showConfirm ? 'text' : 'password'} className="input-field pr-8" {...signupForm.register('confirmPassword', { required: 'Required' })} />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    {signupForm.formState.errors.confirmPassword && <p className="error-text">{signupForm.formState.errors.confirmPassword.message}</p>}
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
                  <input type="checkbox" {...signupForm.register('agreeTerms', { required: true })} className="mt-0.5 rounded" />
                  <span>I agree to the <button type="button" className="text-primary underline">Terms of Service</button> and <button type="button" className="text-primary underline">Privacy Policy</button></span>
                </label>
                <button type="submit" disabled={isLoading} className="w-full btn-primary justify-center py-3 text-base">
                  {isLoading ? (
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : 'Create Account'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}